"use client";

import { createTheme, Theme } from "@mui/material/styles";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import palette from "./customization/palette";
import typography from "./customization/typography";
import theme from "./scss/theme.module.scss";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    "sunset-gradient": true;
    "ocean-gradient": true;
  }
}

function createAppTheme(direction: "rtl" | "ltr" = "ltr") {
  /**
   * set up the theme with color
   */
  const colorTheme: Theme = palette();

  /**
   * set up the theme with color
   */
  const typographyTheme: TypographyOptions = typography(colorTheme);

  return createTheme({
    direction: direction,
    palette: colorTheme.palette,
    typography: typographyTheme,
    breakpoints: {
      values: {
        xx: parseInt(`${theme.breakpointXXNum}`, 10),
        xl: parseInt(`${theme.breakpointXLNum}`, 10),
        lg: parseInt(`${theme.breakpointLGNum}`, 10),
        md: parseInt(`${theme.breakpointMDNum}`, 10),
        sm: parseInt(`${theme.breakpointSMNum}`, 10),
        xs: parseInt(`${theme.breakpointXSNum}`, 10),
        ss: parseInt(`${theme.breakpointSSNum}`, 10),
      },
    },
    components: {
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: "4px",
            fontSize: "13px",
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "sunset-gradient" },
            style: {
              color: theme.midnight500,
              background: "linear-gradient(0, #e89700 0, #f99aa9 100%)",
              border: `2px solid ${theme.midnight500}`,
              "&:hover": {
                color: theme.midnight500,
              },
            },
          },
          {
            props: { variant: "ocean-gradient" },
            style: {
              color: theme.midnight500,
              border: `2px solid ${theme.midnight500}`,
              background: "linear-gradient(90deg, #7dadbb 0, #f99aa9 54%, #e89700 100%)",
              "&:hover": {
                color: theme.midnight500,
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: 14,
            fontFamily: typographyTheme.poppins,
            borderRadius: theme.borderRadius,
            borderColor: "currentcolor",
            borderWidth: "2px",
            padding: "8px 16px",
            textTransform: "none",
            boxShadow: "none",
            cursor: "pointer",
            "&.Mui-disabled": {
              backgroundColor: theme.midnight300,
              "&.MuiButton-containedPrimary": {
                backgroundColor: theme.midnight300,
                borderColor: theme.midnight300,
              },
            },
            "&:hover": {
              borderRadius: theme.borderRadius,
              borderWidth: "2px",
            },
            "&.MuiButton-contained": {
              color: theme.secondaryMain,
              borderStyle: "solid",
            },
            "&.MuiButton-sizeLarge": {
              padding: "16px 32px",
              fontSize: 16,
              lineHeight: "16px",
            },
            "&.MuiButton-sizeMedium": {
              padding: "12px 24px",
              fontSize: 16,
            },
            "&.MuiButton-sizeSmall": {
              padding: "8px 16px",
              fontSize: 14,
            },
            "&.MuiButton-containedPrimary": {
              borderColor: theme.primaryMain,

              "&.Mui-disabled": {
                //borderColor: theme.grey400
              },
            },
            "&.MuiButton-containedSecondary": {
              borderColor: theme.secondaryMain,
            },
            "&.MuiButton-containedWhite": {
              color: theme.black,
              borderColor: theme.white,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            border: "none !important",
            outlined: "none !important",
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: theme.black,
            fontSize: "14px",
            top: "-5px !important",
            "&.MuiInputLabel-shrink": {
              top: "2px !important",
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            "& textarea": {
              padding: "0px !important",
            },
            fontSize: "14px",
          },
          input: {
            "&::placeholder": {
              color: theme.black,
              fontSize: "14px",
              textAlign: direction === "rtl" ? "right" : "left",
              transformOrigin: "right !important",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            background: theme.secondaryMain,
            borderRadius: theme.borderRadius,
            borderColor: theme.black,
            borderWidth: "2px",
            fontSize: "14px",
          },
          input: {
            fontWeight: 400,
            background: theme.secondaryMain,
            padding: "12px 24px",
            fontSize: "14px",
            borderRadius: theme.borderRadius,
            "&::placeholder": {
              fontSize: "14px",
              color: `${theme.primaryMain} !important`,
              opacity: 1,
            },
          },
          inputAdornedStart: {
            paddingLeft: 4,
          },
          notchedOutline: {
            borderRadius: theme.borderRadius,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            background: theme.backgroundColor,
            borderRadius: theme.borderRadius,
            border: `2px solid ${theme.black}`,
            boxShadow: "none",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            "&::before, &::after": {
              borderColor: `${theme.secondaryDark} !important`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            "&.MuiPaper-outlined": {
              border: `1px solid ${theme.secondaryDark}`,
              borderRadius: theme.borderRadius,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: theme.primaryMain,
            paddingTop: "10px",
            paddingBottom: "10px",
            "&.Mui-selected": {
              color: theme.primaryMain,
              backgroundColor: theme.primaryMain,
              "&:hover": {
                backgroundColor: theme.primaryMain,
              },
              "& .MuiListItemIcon-root": {
                color: theme.primaryMain,
              },
            },
            "&:hover": {
              color: theme.primaryMain,
              "& .MuiListItemIcon-root": {
                color: theme.primaryMain,
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: theme.primaryMain,
            fontFamily: typographyTheme.roughwell,
          },
        },
      },
    },
  });
}

export default createAppTheme;
